// Fleet Catalog — software distribution: instalačky (katalog), install šablony,
// skupiny aplikací → skupiny počítačů, deploy + reconcile (drift). Sekce v jednom tabu.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Modal,
  Select,
} from '../../components/ui';
import { Boxes, Download, FileCode, Layers, Plus, Rocket } from 'lucide-react';

interface Pkg { id: number; name: string; version: string; os: string; status: string; size_bytes: number; sha256: string }
interface Tpl { id: number; name: string; os: string; package_name: string; pin_version: string | null; filename: string | null }
interface AppGroup { id: number; name: string; description: string | null; templates: string[]; computer_groups: string[] }

function fmtSize(n: number) { return n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${(n / 1e3).toFixed(0)} kB`; }

export function CatalogTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [deployTpl, setDeployTpl] = useState<Tpl | null>(null);
  const [agOpen, setAgOpen] = useState(false);

  const { data: pkgData } = useQuery({
    queryKey: ['fleet-packages'],
    queryFn: () => api.mcp<{ packages: Pkg[] }>('fleet.package_list', {}),
  });
  const { data: tplData } = useQuery({
    queryKey: ['fleet-templates'],
    queryFn: () =>
      api.mcp<{ templates: Tpl[] }>('fleet.install_template_list', {}),
  });
  const { data: agData } = useQuery({
    queryKey: ['fleet-appgroups'],
    queryFn: () => api.mcp<{ app_groups: AppGroup[] }>('fleet.app_group_list', {}),
  });

  const packages = pkgData?.packages ?? [];
  const templates = tplData?.templates ?? [];
  const appGroups = agData?.app_groups ?? [];

  const statusVariant = (s: string) => s === 'active' ? 'success' : s === 'deprecated' ? 'warning' : 'secondary';

  return (
    <div className='space-y-6'>
      {/* ── Instalačky ── */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-sm flex items-center gap-2'><Boxes size={16} /> {t('fleetCatalog.installersTitle')}</CardTitle>
          <Button size='sm' onClick={() => setImportOpen(true)}><Download size={14} /> {t('fleetCatalog.importFromUrl')}</Button>
        </CardHeader>
        <CardContent>
          {packages.length === 0
            ? <EmptyState icon={<Boxes size={28} />} title={t('fleetCatalog.emptyCatalog')} description={t('fleetCatalog.emptyCatalogDesc')} />
            : (
              <div className='divide-y'>
                {packages.map((p) => (
                  <div key={p.id} className='flex items-center gap-3 py-2 text-sm'>
                    <span className='font-medium min-w-40'>{p.name}</span>
                    <span className='text-gray-500'>v{p.version}</span>
                    <Badge variant='secondary'>{p.os}</Badge>
                    <Badge variant={statusVariant(p.status)}>{p.status}</Badge>
                    <span className='text-gray-400 ml-auto'>{fmtSize(p.size_bytes)}</span>
                    <code className='text-[10px] text-gray-400'>{p.sha256.slice(0, 12)}…</code>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* ── Šablony ── */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-sm flex items-center gap-2'><FileCode size={16} /> {t('fleetCatalog.templatesTitle')}</CardTitle>
          <Button size='sm' onClick={() => setTplOpen(true)}><Plus size={14} /> {t('fleetCatalog.newTemplate')}</Button>
        </CardHeader>
        <CardContent>
          {templates.length === 0
            ? <EmptyState icon={<FileCode size={28} />} title={t('fleetCatalog.noTemplates')} description={t('fleetCatalog.noTemplatesDesc')} />
            : (
              <div className='divide-y'>
                {templates.map((tp) => (
                  <div key={tp.id} className='flex items-center gap-3 py-2 text-sm'>
                    <span className='font-medium min-w-40'>{tp.name}</span>
                    <span className='text-gray-500'>{tp.package_name} {tp.pin_version ? `(v${tp.pin_version})` : '(latest)'}</span>
                    <Badge variant='secondary'>{tp.os}</Badge>
                    <Button size='sm' variant='ghost' className='ml-auto' onClick={() => setDeployTpl(tp)}>
                      <Rocket size={14} /> {t('fleetCatalog.deploy')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* ── Skupiny aplikací → skupiny počítačů ── */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-sm flex items-center gap-2'><Layers size={16} /> {t('fleetCatalog.appGroupsTitle')}</CardTitle>
          <Button size='sm' onClick={() => setAgOpen(true)}><Plus size={14} /> {t('fleetCatalog.newGroup')}</Button>
        </CardHeader>
        <CardContent>
          {appGroups.length === 0
            ? <EmptyState icon={<Layers size={28} />} title={t('fleetCatalog.noAppGroups')} description={t('fleetCatalog.noAppGroupsDesc')} />
            : (
              <div className='space-y-3'>
                {appGroups.map((g) => <AppGroupCard key={g.id} g={g} templates={templates} />)}
              </div>
            )}
        </CardContent>
      </Card>

      {importOpen && <ImportModal onClose={() => { setImportOpen(false); qc.invalidateQueries({ queryKey: ['fleet-packages'] }); }} />}
      {tplOpen && <TemplateModal packages={packages} onClose={() => { setTplOpen(false); qc.invalidateQueries({ queryKey: ['fleet-templates'] }); }} />}
      {deployTpl && <DeployModal tpl={deployTpl} onClose={() => setDeployTpl(null)} />}
      {agOpen && <AppGroupModal onClose={() => { setAgOpen(false); qc.invalidateQueries({ queryKey: ['fleet-appgroups'] }); }} />}
    </div>
  );
}

function AppGroupCard({ g, templates }: { g: AppGroup; templates: Tpl[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tpl, setTpl] = useState('');
  const [fg, setFg] = useState('');
  const inval = () => qc.invalidateQueries({ queryKey: ['fleet-appgroups'] });
  const { data: groupsData } = useQuery({
    queryKey: ['fleet-groups-names'],
    queryFn: async () => {
      const r = await api.mcp<{ groups: Array<{ name: string }> }>('fleet.group_list', {});
      return r.groups ?? [];
    },
  });
  const computerGroups = groupsData ?? [];

  const addTpl = useMutation({
    mutationFn: () => api.mcp('fleet.app_group_add', { app_group: g.name, template: tpl }),
    onSuccess: () => { toast.success(t('fleetCatalog.templateAdded')); setTpl(''); inval(); }, onError: () => toast.error(t('fleetCatalog.error')),
  });
  const assign = useMutation({
    mutationFn: () => api.mcp('fleet.app_group_assign', { app_group: g.name, fleet_group: fg }),
    onSuccess: () => { toast.success(t('fleetCatalog.assignedToGroup')); setFg(''); inval(); }, onError: () => toast.error(t('fleetCatalog.error')),
  });

  return (
    <div className='border rounded p-3 space-y-2'>
      <div className='font-medium text-sm'>{g.name}{g.description ? <span className='text-gray-500 font-normal'> — {g.description}</span> : null}</div>
      <div className='text-xs'>
        <span className='text-gray-500'>{t('fleetCatalog.appsLabel')}</span> {g.templates.length ? g.templates.map((tp) => <Badge key={tp} variant='secondary' className='mr-1'>{tp}</Badge>) : <span className='text-gray-400'>{t('fleetCatalog.none')}</span>}
      </div>
      <div className='text-xs'>
        <span className='text-gray-500'>{t('fleetCatalog.computerGroupsLabel')}</span> {g.computer_groups.length ? g.computer_groups.map((c) => <Badge key={c} variant='success' className='mr-1'>{c}</Badge>) : <span className='text-gray-400'>{t('fleetCatalog.unassigned')}</span>}
      </div>
      <div className='flex flex-wrap gap-2 pt-1'>
        <Select size='sm' value={tpl} onChange={(e) => setTpl(e.target.value)}>
          <option value=''>{t('fleetCatalog.addTemplateOption')}</option>
          {templates.map((tp) => <option key={tp.id} value={tp.name}>{tp.name}</option>)}
        </Select>
        <Button size='sm' variant='ghost' disabled={!tpl || addTpl.isPending} onClick={() => addTpl.mutate()}>{t('fleetCatalog.add')}</Button>
        <Select size='sm' value={fg} onChange={(e) => setFg(e.target.value)}>
          <option value=''>{t('fleetCatalog.assignToGroupOption')}</option>
          {computerGroups.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </Select>
        <Button size='sm' variant='ghost' disabled={!fg || assign.isPending} onClick={() => assign.mutate()}>{t('fleetCatalog.assignButton')}</Button>
      </div>
    </div>
  );
}

function ImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: '', version: '', os: 'windows', source_url: '', filename: '' });
  const m = useMutation({
    mutationFn: () => api.mcp('fleet.package_import', f),
    onSuccess: (r: { ok?: boolean; error?: string }) => { r?.ok ? (toast.success(t('fleetCatalog.imported')), onClose()) : toast.error(r?.error ?? t('fleetCatalog.importError')); },
    onError: () => toast.error(t('fleetCatalog.importFailed')),
  });
  const set = (k: string, v: string) => setF({ ...f, [k]: v });
  return (
    <Modal open onClose={onClose} title={t('fleetCatalog.importTitle')}>
      <div className='space-y-3'>
        {[['name', t('fleetCatalog.phName')], ['version', t('fleetCatalog.phVersion')], ['source_url', t('fleetCatalog.phUrl')], ['filename', t('fleetCatalog.phFilename')]].map(([k, ph]) => (
          <input key={k} className='w-full border rounded px-2 py-1' placeholder={ph} value={(f as Record<string, string>)[k]} onChange={(e) => set(k, e.target.value)} />
        ))}
        <Select className='w-full' value={f.os} onChange={(e) => set('os', e.target.value)}>
          <option value='windows'>Windows</option><option value='linux'>Linux</option>
        </Select>
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!f.name || !f.source_url || m.isPending} onClick={() => m.mutate()}>{m.isPending ? t('fleetCatalog.downloading') : 'Import'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplateModal({ packages, onClose }: { packages: Pkg[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: '', package_name: '', os: 'windows', filename: '', silent_args: '' });
  const m = useMutation({
    mutationFn: () => api.mcp('fleet.template_create', { ...f, silent_args: f.silent_args.split(' ').filter(Boolean) }),
    onSuccess: (r: { ok?: boolean }) => { r?.ok ? (toast.success(t('fleetCatalog.templateCreated')), onClose()) : toast.error(t('fleetCatalog.error')); },
    onError: () => toast.error(t('fleetCatalog.error')),
  });
  const set = (k: string, v: string) => setF({ ...f, [k]: v });
  const pkgNames = [...new Set(packages.map((p) => p.name))];
  return (
    <Modal open onClose={onClose} title={t('fleetCatalog.newTemplateTitle')}>
      <div className='space-y-3'>
        <input className='w-full border rounded px-2 py-1' placeholder={t('fleetCatalog.phTemplateName')} value={f.name} onChange={(e) => set('name', e.target.value)} />
        <Select className='w-full' value={f.package_name} onChange={(e) => set('package_name', e.target.value)}>
          <option value=''>{t('fleetCatalog.selectPackage')}</option>
          {pkgNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </Select>
        <input className='w-full border rounded px-2 py-1' placeholder={t('fleetCatalog.phTargetFile')} value={f.filename} onChange={(e) => set('filename', e.target.value)} />
        <input className='w-full border rounded px-2 py-1' placeholder={t('fleetCatalog.phSilentArgs')} value={f.silent_args} onChange={(e) => set('silent_args', e.target.value)} />
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!f.name || !f.package_name || m.isPending} onClick={() => m.mutate()}>{t('fleetCatalog.create')}</Button>
        </div>
      </div>
    </Modal>
  );
}

function DeployModal({ tpl, onClose }: { tpl: Tpl; onClose: () => void }) {
  const { t } = useTranslation();
  const [agentId, setAgentId] = useState('');
  const { data: agentsData } = useQuery({
    queryKey: ['fleet-agents-min'],
    queryFn: () =>
      api.mcp<Array<{ agent_id: string; hostname: string }>>('fleet.agent_list', {}),
  });
  const agents = agentsData ?? [];
  const m = useMutation({
    mutationFn: () => api.mcp('fleet.template_deploy', { template: tpl.name, agent_id: agentId }),
    onSuccess: (r: { ok?: boolean; error?: string }) => { r?.ok ? (toast.success(t('fleetCatalog.deployed')), onClose()) : toast.error(r?.error ?? t('fleetCatalog.error')); },
    onError: () => toast.error(t('fleetCatalog.deployFailed')),
  });
  return (
    <Modal open onClose={onClose} title={t('fleetCatalog.deployTitle', { name: tpl.name })}>
      <div className='space-y-3'>
        <p className='text-sm text-gray-600'>{t('fleetCatalog.deployDesc')}</p>
        <Select className='w-full' value={agentId} onChange={(e) => setAgentId(e.target.value)}>
          <option value=''>{t('fleetCatalog.selectTarget')}</option>
          {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.hostname} ({a.agent_id})</option>)}
        </Select>
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!agentId || m.isPending} onClick={() => m.mutate()}><Rocket size={14} /> {t('fleetCatalog.deploy')}</Button>
        </div>
      </div>
    </Modal>
  );
}

function AppGroupModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: '', description: '' });
  const m = useMutation({
    mutationFn: () => api.mcp('fleet.app_group_create', f),
    onSuccess: (r: { ok?: boolean }) => { r?.ok ? (toast.success(t('fleetCatalog.appGroupCreated')), onClose()) : toast.error(t('fleetCatalog.error')); },
    onError: () => toast.error(t('fleetCatalog.error')),
  });
  return (
    <Modal open onClose={onClose} title={t('fleetCatalog.newAppGroupTitle')}>
      <div className='space-y-3'>
        <input className='w-full border rounded px-2 py-1' placeholder={t('fleetCatalog.phGroupName')} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className='w-full border rounded px-2 py-1' placeholder={t('fleetCatalog.phGroupDesc')} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!f.name || m.isPending} onClick={() => m.mutate()}>{t('fleetCatalog.create')}</Button>
        </div>
      </div>
    </Modal>
  );
}
